<!-- src/components/forms/completed/FatigueManagementPlanCompleted.vue -->
<template>
  <div id="printable-form-content" class="space-y-16 animate-fadeIn text-left print:animate-none print:opacity-100 print:text-black">
    
    <!-- 1. DETAILS OF EXCEEDANCE -->
    <section class="print:break-inside-avoid">
      <div class="flex items-center gap-4 mb-8 text-left">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1] print:border-slate-800">
          <ClockIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Details of Exceedance</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner text-left print:bg-slate-50 print:border-slate-200">
        <div class="space-y-6">
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <p class="micro-label text-slate-400 mb-2">Subject Personnel</p>
            <p class="text-lg font-black text-[#0A1630] uppercase leading-none">{{ flattenedData.subjectName }}</p>
          </div>
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <p class="micro-label text-slate-400 mb-2">Sentinel Number</p>
            <p class="text-lg font-black text-[#0A1630] uppercase leading-none font-mono tracking-tighter">{{ flattenedData.sentinelNumber }}</p>
          </div>
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <p class="micro-label text-slate-400 mb-2">Plan Effective Date</p>
            <p class="text-lg font-black text-[#0A1630] uppercase leading-none">{{ formatDate(flattenedData.date, 'dateOnly') }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-6">
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col justify-center text-center">
            <p class="micro-label text-slate-400 mb-2">Work Hours</p>
            <p class="text-3xl font-black text-[#0A1630]">{{ flattenedData.workHours }}</p>
          </div>
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col justify-center text-center">
            <p class="micro-label text-slate-400 mb-2">Travel Hours</p>
            <p class="text-3xl font-black text-[#0A1630]">{{ flattenedData.travelHours }}</p>
          </div>
          
          <div class="bg-[#0A1630] p-8 rounded-3xl shadow-xl flex flex-col justify-center text-center col-span-2 relative overflow-hidden print:bg-slate-900">
            <div class="relative z-10">
              <p class="micro-label text-[#20FCF1] mb-3">Calculated Risk Index</p>
              <h4 :class="riskColorClass" class="text-5xl font-black uppercase tracking-tighter leading-none mb-3">
                {{ flattenedData.riskLevel }}
              </h4>
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest border-t border-white/5 pt-3 print:border-slate-700">
                Total Door-to-Door: {{ flattenedData.totalHours }} hrs
              </p>
            </div>
            <ShieldCheckIcon class="absolute -right-4 -bottom-4 h-24 w-24 text-white opacity-5" />
          </div>
        </div>
      </div>
    </section>

    <!-- 2. RISK ANALYSIS & STRATEGY -->
    <section class="print:break-inside-avoid">
      <div class="flex items-center gap-4 mb-8 text-left">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <ShieldCheckIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Operational Strategy</h2>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div v-if="flattenedData.reason?.length" class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-xl">
          <p class="micro-label text-slate-400 mb-6 border-b pb-3">Primary Exceedance Reasons</p>
          <ul class="space-y-4">
            <li v-for="r in flattenedData.reason" :key="r" class="flex items-center gap-3">
              <div class="w-2 h-2 rounded-full bg-[#20FCF1] print:bg-slate-400"></div>
              <span class="text-xs font-black text-[#0A1630] uppercase tracking-tight">{{ r }}</span>
            </li>
          </ul>
        </div>

        <div v-if="flattenedData.action?.length" class="bg-[#EBFDF8] p-8 rounded-[32px] border border-[#20FCF1]/20 shadow-xl print:bg-emerald-50 print:border-emerald-100">
          <p class="micro-label text-[#20FCF1] mb-6 border-b border-[#20FCF1]/10 pb-3 print:text-emerald-600 print:border-emerald-200">Mitigating Actions & Controls</p>
          <ul class="space-y-4">
            <li v-for="a in flattenedData.action" :key="a" class="flex items-center gap-3">
              <CheckIcon class="h-4 w-4 text-[#20FCF1] print:text-emerald-600" />
              <span class="text-xs font-black text-[#0A1630] uppercase tracking-tight">{{ a }}</span>
            </li>
          </ul>
        </div>

        <div v-if="flattenedData.advice" class="lg:col-span-2 bg-slate-50 p-10 rounded-[40px] border border-slate-100 shadow-inner print:bg-slate-50 print:border-slate-200">
          <p class="micro-label text-slate-400 mb-4 uppercase">Professional Advice & Controls</p>
          <p class="text-sm font-medium text-slate-700 leading-relaxed italic">"{{ flattenedData.advice }}"</p>
        </div>

        <div v-if="flattenedData.nextReviewDate" class="lg:col-span-2 flex items-center justify-center p-6 bg-[#0A1630] rounded-[32px] shadow-2xl border-b-4 border-amber-500 print:bg-slate-900">
           <p class="text-[10px] font-black text-white uppercase tracking-[0.2em]">
             Scheduled Protocol Review: <span class="text-amber-500 ml-2">{{ formatDate(flattenedData.nextReviewDate, 'dateOnly') }}</span>
           </p>
        </div>
      </div>
    </section>

    <!-- 3. AUTHORISATION SEALS -->
    <section class="border-t pt-12 print:break-inside-avoid">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
        <!-- Assessor Seal -->
        <div class="bg-white p-10 rounded-[40px] border border-slate-200 shadow-xl flex flex-col items-center text-center">
          <p class="micro-label text-slate-400 mb-8">Assessor Authorisation Seal</p>
          <div class="h-24 flex items-center justify-center">
            <img v-if="flattenedData.assessorSig" :src="flattenedData.assessorSig" alt="Assessor Seal" class="max-h-24 object-contain grayscale">
            <div v-else class="text-[10px] font-black text-slate-300 uppercase italic">Seal Pending</div>
          </div>
          <div class="w-full h-px bg-slate-100 my-8"></div>
          <p class="text-sm font-black text-[#0A1630] uppercase tracking-tight">{{ flattenedData.assessorName }}</p>
          <p class="text-[9px] font-bold text-slate-400 uppercase mt-1">Registry Initialised: {{ formatDate(submissionData.submittedAt) }}</p>
        </div>

        <!-- Operative Counter-Sign Box -->
        <div class="bg-[#EBFDF8] p-10 rounded-[40px] border border-[#20FCF1]/30 shadow-xl flex flex-col items-center text-center print:bg-emerald-50 print:border-emerald-100 relative">
          <p class="micro-label text-[#20FCF1] mb-8 uppercase print:text-emerald-600">Personnel Counter-Signature</p>
          
          <div class="h-24 flex items-center justify-center w-full">
            <!-- 1. Permanent Seal (Already signed) -->
            <img v-if="flattenedData.operativeSig" :src="flattenedData.operativeSig" alt="Operative Seal" class="max-h-24 object-contain">
            
            <!-- 2. Live Handshake -->
            <div v-else-if="isTargetPersonnel && profileSignature" class="flex flex-col items-center gap-4 animate-fadeIn">
               <img :src="profileSignature" alt="Verified Profile Seal" class="max-h-20 object-contain grayscale opacity-70">
               <button 
                @click="$emit('authorise', profileSignature)" 
                :disabled="isUpdating"
                class="bg-[#0A1630] text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 disabled:opacity-50"
               >
                 {{ isUpdating ? 'Processing...' : 'Agree & Authorise' }}
               </button>
            </div>

            <!-- 3. Pending State -->
            <div v-else class="text-[10px] font-black text-[#20FCF1]/40 uppercase italic">Awaiting Operative Review</div>
          </div>

          <div class="w-full h-px bg-[#20FCF1]/10 my-8 print:bg-emerald-200/30"></div>
          <p class="text-sm font-black text-[#0A1630] uppercase tracking-tight">{{ flattenedData.subjectName }}</p>
          
          <div class="mt-1">
            <template v-if="flattenedData.operativeSig">
              <p class="text-[9px] font-bold text-[#20FCF1] uppercase">Status: Legally Acknowledged</p>
              <!-- 🛡️ ADDED: Legal Acknowledgement Timestamp -->
              <p v-if="flattenedData.operativeAuthorisedAt" class="text-[9px] font-bold text-slate-400 uppercase mt-1">
                Legal Acknowledgement: {{ formatDate(flattenedData.operativeAuthorisedAt) }}
              </p>
            </template>
            <div v-else-if="isTargetPersonnel" class="text-[9px] font-bold text-amber-500 uppercase flex items-center gap-1.5 justify-center">
               <div class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div>
               Verification Required
            </div>
            <p v-else class="text-[9px] font-bold text-rose-400 uppercase">Action Required: Signature Pending</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { ClockIcon, ShieldCheckIcon, CheckIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  isTargetPersonnel: { type: Boolean, default: false },
  profileSignature: { type: String, default: null },
  isUpdating: { type: Boolean, default: false }
});

defineEmits(['authorise']);

const flattenedData = computed(() => {
  const s = props.submissionData;
  const f = s.formData || {};
  const getArray = (val) => Array.isArray(val) ? val : (val ? [val] : []);

  return {
    subjectName: s.subjectPersonnelName || f.subjectPersonnelName || 'PERSONNEL N/A',
    sentinelNumber: s.subjectSentinelNumber || f.subjectSentinelNumber || 'N/A',
    assessorName: s.completingPersonName || f.completingPersonName || 'ASSESSOR N/A',
    date: s.medicalTriggerDate || f.medicalTriggerDate || s.submittedAt || null,
    workHours: s.workHours ?? f.workHours ?? 0,
    travelHours: s.travelHours ?? f.travelHours ?? 0,
    totalHours: s.totalHours ?? f.totalHours ?? 0,
    riskLevel: s.calculatedRiskLevel || f.calculatedRiskLevel || 'LOW',
    reason: getArray(s.reasonForPlan || f.reasonForPlan),
    action: getArray(s.actionPlan || f.actionPlan),
    advice: s.provideAdvice || f.provideAdvice || null,
    nextReviewDate: s.nextReviewDate || f.nextReviewDate || null,
    assessorSig: s.completerSignatureDataUrl || f.completerSignatureDataUrl || null,
    operativeSig: s.operativeSignatureDataUrl || f.operativeSignatureDataUrl || s.personnelSignatureDataUrl || f.personnelSignatureDataUrl || null,
    // 🛡️ EXTRACT: Handshake Timestamp
    operativeAuthorisedAt: s.operativeAuthorisedAt || f.operativeAuthorisedAt || null
  };
});

const riskColorClass = computed(() => {
  const level = String(flattenedData.value.riskLevel).toUpperCase();
  if (level.includes('HIGH')) return 'text-rose-500';
  if (level.includes('MEDIUM')) return 'text-amber-500';
  return 'text-[#20FCF1]';
});
</script>

<style scoped>
.micro-label { @apply text-[10px] font-black uppercase tracking-[0.2em]; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

@media print {
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .animate-fadeIn, .animate-pulse { animation: none !important; opacity: 1 !important; transform: none !important; }
  .bg-[#0A1630] { background-color: #0A1630 !important; color: white !important; }
}
</style>